/**
 * facebook-loader.ts — fetches public/api/facebook-{city}.json for the
 * client. Sibling to incidents-loader.ts; same fail-soft contract.
 *
 * Why a separate loader instead of folding into incidents-loader:
 *   - Different endpoint (static JSON, not the Worker route).
 *   - Different freshness contract (6h cron vs 2h Worker cron).
 *   - Lets future surfaces opt into FB items without paying the
 *     incidents fetch, e.g. an admin debug panel that lists raw FB
 *     posts for tuning the pages curation list.
 *
 * The output shape is RawIncident[] so consumers can union it directly
 * with the incidents pool — the FB pipeline writes exactly the same
 * field names (id, suburb_id, coord, category, severity, headline,
 * source, url, published_at). The only differentiator is `type:
 * "facebook"`, which the bubble layer treats as "non-video" (default
 * text rendering) and the dedup module ignores.
 *
 * Network failure is non-fatal: returns empty array and logs once. The
 * caller (incidents-loader) merges []-result into rawAll without
 * affecting the rest of the pipeline.
 */

import type { RawIncident } from "./incident-dedup";

export type CityKey = "hyd" | "mel" | "blr" | "syd";

export interface LoadedFacebookFeed {
  city: CityKey;
  ready: boolean;
  generatedAt: string | null;
  items: ReadonlyArray<RawIncident>;
}

const _cache: Map<CityKey, LoadedFacebookFeed> = new Map();
const _inFlight: Map<CityKey, Promise<LoadedFacebookFeed>> = new Map();

const EMPTY: (city: CityKey) => LoadedFacebookFeed = (city) => ({
  city,
  ready: false,
  generatedAt: null,
  items: [],
});

/**
 * Fetch and cache the Facebook feed for a city.
 *
 * Endpoint: `/api/facebook-{city}.json` — a static asset on Cloudflare
 * Pages, refreshed by the facebook-refresh workflow every 6h. The asset
 * always exists (the fetcher writes an empty-but-valid JSON when no
 * pages are configured), so a 404 here is genuinely an error.
 *
 * Returns shape:
 *   - On success: { ready: true, items: [...], generatedAt: ISO string }
 *   - On failure (network, parse, 404): { ready: false, items: [] }
 *
 * Idempotent across a session — second call returns the cached value.
 */
export function loadFacebook(city: CityKey): Promise<LoadedFacebookFeed> {
  const cached = _cache.get(city);
  if (cached) return Promise.resolve(cached);
  const inFlight = _inFlight.get(city);
  if (inFlight) return inFlight;

  const p: Promise<LoadedFacebookFeed> = (async () => {
    try {
      let json: any = null;
      try {
        const res = await fetch(`/api/facebook-${city}.json`, {
          // Static asset — let the browser honour Cloudflare's cache-
          // control header. Same approach as incidents-loader (default
          // cache policy).
          signal: AbortSignal.timeout?.(8000),
        });
        if (res.ok) {
          json = await res.json();
        } else if (res.status === 404) {
          // First-deploy state before fetch-facebook.mjs has ever run.
          // Treat as empty silently — no curation set up yet.
          json = null;
        } else {
          console.warn(
            `[HYPERLOCAL/facebook] city=${city} HTTP ${res.status} from static endpoint`,
          );
        }
      } catch (err) {
        // Network blip, AbortSignal timeout, parse error. Return empty
        // — the incidents path is the load-bearing surface; FB is
        // additive, so a fetch failure here must NOT cascade.
        console.warn(`[HYPERLOCAL/facebook] city=${city} fetch failed:`, err);
      }

      if (!json) {
        const empty = EMPTY(city);
        _cache.set(city, empty);
        return empty;
      }

      const items: RawIncident[] = Array.isArray(json.items) ? json.items : [];
      const feed: LoadedFacebookFeed = {
        city,
        ready: true,
        generatedAt:
          typeof json.generated_at === "string" ? json.generated_at : null,
        items,
      };
      _cache.set(city, feed);
      console.log(
        `[HYPERLOCAL/facebook] city=${city} loaded (items=${items.length}, generated_at=${feed.generatedAt ?? "?"})`,
      );
      return feed;
    } catch (err) {
      console.warn(`[HYPERLOCAL/facebook] city=${city} load failed`, err);
      const empty = EMPTY(city);
      _cache.set(city, empty);
      return empty;
    } finally {
      _inFlight.delete(city);
    }
  })();

  _inFlight.set(city, p);
  return p;
}

/**
 * Fetch the items array for a city — convenience for callers (like
 * incidents-loader) that only need the raw items to union into a
 * larger pool.
 *
 * Always resolves; never throws. Empty array on any failure path.
 */
export async function fetchFacebookItems(
  city: CityKey,
): Promise<ReadonlyArray<RawIncident>> {
  try {
    const feed = await loadFacebook(city);
    return feed.items;
  } catch {
    return [];
  }
}
